/**
 * ComplianceScore Component
 * Circular progress indicator with color-coded compliance status
 *
 * Part of Sprint Day 5-6: Compliance Dashboard UI
 */

import { cn } from "@/lib/utils";
import { CheckCircle2, XCircle, AlertTriangle, HelpCircle } from "lucide-react";

interface ComplianceScoreProps {
  score: number | null;
  status: 'pass' | 'fail' | 'needs_review' | 'not_started' | null;
  summary?: {
    critical: number;
    warning: number;
    info: number;
  };
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showSummary?: boolean;
  className?: string;
}

export function ComplianceScore({
  score,
  status,
  summary,
  size = 'md',
  showLabel = true,
  showSummary = true,
  className
}: ComplianceScoreProps) {
  const sizeConfig = {
    sm: {
      container: 'h-16 w-16',
      text: 'text-lg',
      strokeWidth: 6,
      radius: 28
    },
    md: {
      container: 'h-24 w-24',
      text: 'text-2xl',
      strokeWidth: 8,
      radius: 42
    },
    lg: {
      container: 'h-32 w-32',
      text: 'text-4xl',
      strokeWidth: 10,
      radius: 56
    }
  };

  const statusConfig = {
    pass: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      stroke: 'stroke-green-500',
      label: 'Compliant',
      Icon: CheckCircle2
    },
    fail: {
      bg: 'bg-red-100',
      text: 'text-red-700',
      stroke: 'stroke-red-500',
      label: 'Non-Compliant',
      Icon: XCircle
    },
    needs_review: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-700',
      stroke: 'stroke-yellow-500',
      label: 'Needs Review',
      Icon: AlertTriangle
    },
    not_started: {
      bg: 'bg-gray-100',
      text: 'text-gray-500',
      stroke: 'stroke-gray-300',
      label: 'Not Checked',
      Icon: HelpCircle
    }
  };

  const config = sizeConfig[size];
  const statusStyle = statusConfig[status || 'not_started'];
  const Icon = statusStyle.Icon;

  // Calculate SVG circle parameters
  const viewBoxSize = (config.radius * 2) + config.strokeWidth;
  const center = viewBoxSize / 2;
  const circumference = 2 * Math.PI * config.radius;
  const displayScore = score ?? 0;
  const strokeDashoffset = circumference - (displayScore / 100) * circumference;

  // Not started state
  if (!status || status === 'not_started' || score === null) {
    return (
      <div className={cn("flex flex-col items-center gap-2", className)}>
        <div className={cn("relative flex items-center justify-center", config.container)}>
          <div className="absolute inset-0 flex items-center justify-center">
            <HelpCircle className={cn("text-gray-400", size === 'sm' ? 'h-6 w-6' : size === 'md' ? 'h-8 w-8' : 'h-12 w-12')} />
          </div>
          <svg className="w-full h-full -rotate-90">
            <circle
              cx={center}
              cy={center}
              r={config.radius}
              className="fill-none stroke-gray-200"
              strokeWidth={config.strokeWidth}
              style={{ viewBox: `0 0 ${viewBoxSize} ${viewBoxSize}` } as any}
            />
          </svg>
        </div>
        {showLabel && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-500">
            <HelpCircle className="h-4 w-4" />
            Not Checked
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      {/* Circular Score */}
      <div className={cn("relative", config.container)}>
        <svg
          className="w-full h-full -rotate-90"
          viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        >
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={config.radius}
            className="fill-none stroke-gray-200"
            strokeWidth={config.strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={config.radius}
            className={cn("fill-none", statusStyle.stroke)}
            strokeWidth={config.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{
              transition: 'stroke-dashoffset 0.5s ease-in-out'
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("font-bold", config.text, statusStyle.text)}>
            {displayScore}%
          </span>
        </div>
      </div>

      {/* Status Badge */}
      {showLabel && (
        <div className={cn(
          "flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium",
          statusStyle.bg,
          statusStyle.text
        )}>
          <Icon className="h-4 w-4" />
          {statusStyle.label}
        </div>
      )}

      {/* Summary breakdown */}
      {showSummary && summary && (
        <div className="flex gap-3 text-xs">
          {summary.critical > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              {summary.critical} Critical
            </span>
          )}
          {summary.warning > 0 && (
            <span className="flex items-center gap-1 text-yellow-600">
              <span className="h-2 w-2 rounded-full bg-yellow-500" />
              {summary.warning} Warning
            </span>
          )}
          {summary.info > 0 && (
            <span className="flex items-center gap-1 text-blue-600">
              <span className="h-2 w-2 rounded-full bg-blue-500" />
              {summary.info} Info
            </span>
          )}
          {summary.critical === 0 && summary.warning === 0 && summary.info === 0 && (
            <span className="text-green-600">No issues found</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact status badge for use in tables and lists
 */
interface ComplianceStatusBadgeProps {
  status: 'pass' | 'fail' | 'needs_review' | 'not_started' | null;
  score?: number | null;
  size?: 'sm' | 'md';
  className?: string;
}

export function ComplianceStatusBadge({
  status,
  score,
  size = 'md',
  className
}: ComplianceStatusBadgeProps) {
  const statusConfig = {
    pass: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
      label: 'Compliant',
      Icon: CheckCircle2
    },
    fail: {
      bg: 'bg-red-100',
      text: 'text-red-700',
      border: 'border-red-200',
      label: 'Non-Compliant',
      Icon: XCircle
    },
    needs_review: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-700',
      border: 'border-yellow-200',
      label: 'Needs Review',
      Icon: AlertTriangle
    },
    not_started: {
      bg: 'bg-gray-100',
      text: 'text-gray-500',
      border: 'border-gray-200',
      label: 'Not Checked',
      Icon: HelpCircle
    }
  };

  const config = statusConfig[status || 'not_started'];
  const Icon = config.Icon;

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1'
  };

  return (
    <div className={cn(
      "inline-flex items-center gap-1 rounded-full border font-medium",
      config.bg,
      config.text,
      config.border,
      sizeClasses[size],
      className
    )}>
      <Icon className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />
      <span>{score !== null && score !== undefined ? `${score}%` : config.label}</span>
    </div>
  );
}

export default ComplianceScore;
